import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Download, Award } from 'lucide-react';

export default function Certificate() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const canvasRef = useRef(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.getUserDiploma(courseId)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Diploma no disponible'));
  }, [courseId]);

  useEffect(() => {
    if (!data) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const template = data.template;

    const draw = (imageOrNull) => {
      const W = 1200;
      const H = 850;
      canvas.width = W;
      canvas.height = H;
      // background
      if (imageOrNull) {
        ctx.drawImage(imageOrNull, 0, 0, W, H);
      } else {
        // Default beige diploma
        ctx.fillStyle = '#f5eedb';
        ctx.fillRect(0, 0, W, H);
        ctx.strokeStyle = '#DC143C';
        ctx.lineWidth = 8;
        ctx.strokeRect(20, 20, W - 40, H - 40);
        ctx.fillStyle = '#1F2937';
        ctx.font = 'bold 56px Georgia';
        ctx.textAlign = 'center';
        ctx.fillText('DIPLOMA DE PARTICIPACIÓN', W / 2, 160);
        ctx.font = '24px Georgia';
        ctx.fillText('Se otorga a:', W / 2, 280);
        ctx.font = '20px Georgia';
        ctx.fillText('por completar exitosamente el curso:', W / 2, 540);
        ctx.font = 'bold 32px Georgia';
        ctx.fillStyle = '#DC143C';
        ctx.fillText(`"${data.course_title}"`, W / 2, 600);
      }
      // Name overlay
      const x = ((template?.name_position_x ?? 50) / 100) * W;
      const y = ((template?.name_position_y ?? 50) / 100) * H;
      ctx.fillStyle = template?.font_color || '#000000';
      ctx.font = `bold ${(template?.font_size ?? 48)}px Georgia`;
      ctx.textAlign = 'center';
      ctx.fillText(data.user_name, x, y);
    };

    if (template?.template_url) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => draw(img);
      img.onerror = () => draw(null);
      // Usar el proxy del backend: evita CORS taint del canvas y soporta URLs de Drive
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      img.src = `${apiBase}/api/diploma/proxy-image?url=${encodeURIComponent(template.template_url)}`;
    } else {
      draw(null);
    }
  }, [data]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const link = document.createElement('a');
      link.download = `diploma_${data.course_title}_${data.user_name}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      toast.error('No se puede descargar (problema CORS con la imagen)');
    }
  };

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto p-6 text-center">
          <Award className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-700 mb-4">{error}</p>
          <Button variant="outline" onClick={() => navigate('/academia')}>Volver</Button>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) return <DashboardLayout><div className="p-8">Cargando...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="certificate-page">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" onClick={() => navigate('/academia')} className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" /> Volver
          </Button>
          <Button onClick={handleDownload} data-testid="download-certificate-button" className="bg-[#DC143C] hover:bg-[#B91C1C] text-white flex items-center gap-2">
            <Download className="h-4 w-4" /> Descargar PNG
          </Button>
        </div>

        <h1 className="text-2xl font-bold mb-2 uppercase flex items-center gap-3">
          <Award className="h-7 w-7 text-[#DC143C]" />
          Diploma
        </h1>
        <p className="text-gray-600 mb-6">{data.course_title}</p>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 overflow-x-auto">
          <canvas ref={canvasRef} className="w-full h-auto" data-testid="certificate-canvas" />
        </div>
      </div>
    </DashboardLayout>
  );
}
